import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOnboardingStatusWithSession } from "@/hooks/useOnboardingStatus";
import { useActiveCompany } from "@/contexts/ActiveCompanyContext";
import { fetchEmployerApplications } from "@/hooks/useEmployerApplications";
import { supabase } from "@/integrations/supabase/client";

const scheduleIdleTask = (task: () => void) => {
  if (typeof window === "undefined") return;

  const idleWindow = window as typeof window & {
    requestIdleCallback?: (cb: IdleRequestCallback) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (idleWindow.requestIdleCallback) {
    const id = idleWindow.requestIdleCallback(() => task());
    return () => idleWindow.cancelIdleCallback?.(id);
  }

  const timeout = window.setTimeout(task, 200);
  return () => window.clearTimeout(timeout);
};

const prefetchCompanySettings = async (companyId: string) => {
  const { data, error } = await supabase
    .from("companies")
    .select("auto_screen_applications")
    .eq("id", companyId)
    .single();

  if (error) throw error;
  return data;
};

export function PerformancePrefetcher() {
  const queryClient = useQueryClient();
  const { role } = useOnboardingStatusWithSession();
  const { companies, activeCompany } = useActiveCompany();

  const prefetchedModules = useRef(new Set<string>());
  const prefetchedCompanies = useRef(new Set<string>());

  useEffect(() => {
    const cleanup = scheduleIdleTask(() => {
      if (!prefetchedModules.current.has("Dashboard")) {
        prefetchedModules.current.add("Dashboard");
        import("../../pages/Dashboard");
      }
    });

    return cleanup;
  }, []);

  const fallbackCompanyId = companies[0]?.company.id;
  const companyId = activeCompany?.company.id ?? fallbackCompanyId;

  useEffect(() => {
    if (role !== "employer" || !companyId) {
      return;
    }

    if (prefetchedCompanies.current.has(companyId)) {
      return;
    }
    prefetchedCompanies.current.add(companyId);

    const cleanup = scheduleIdleTask(() => {
      if (!prefetchedModules.current.has("EmployerATS")) {
        prefetchedModules.current.add("EmployerATS");
        import("../../pages/EmployerATS");
      }

      queryClient.prefetchQuery({
        queryKey: ["employer-applications", { companyId, search: "" }],
        queryFn: () => fetchEmployerApplications({ companyId, search: "" }),
      });

      queryClient.prefetchQuery({
        queryKey: ["company-settings", companyId],
        queryFn: () => prefetchCompanySettings(companyId),
      });
    });

    return cleanup;
  }, [role, companyId, queryClient]);

  return null;
}
